import { useEffect, useState } from 'react';
import { useParams, Link } from 'react-router';
import { invitationService } from '../lib/api-service';
import { useAuth } from '../lib/auth';
import { CheckCircle2, XCircle, Loader2, Mail } from 'lucide-react';

export default function InvitationResponse() {
  const { token } = useParams();
  const { user } = useAuth();
  const [loading, setLoading] = useState(true);
  const [invitation, setInvitation] = useState<{
    activity_title: string;
    activity_slug: string;
    email: string;
    status: string;
  } | null>(null);
  const [error, setError] = useState('');
  const [responding, setResponding] = useState(false);
  const [declineReason, setDeclineReason] = useState('');
  const [done, setDone] = useState<'accepted' | 'declined' | null>(null);

  useEffect(() => {
    if (!token) return;
    invitationService.getDetails(token)
      .then(setInvitation)
      .catch(() => setError('Invitația nu a fost găsită sau a expirat.'))
      .finally(() => setLoading(false));
  }, [token]);

  async function handleRespond(action: 'accept' | 'decline') {
    if (!token) return;
    setResponding(true);
    setError('');
    try {
      await invitationService.respond(token, {
        action,
        reason: action === 'decline' ? declineReason : undefined,
      });
      setDone(action === 'accept' ? 'accepted' : 'declined');
    } catch (err: any) {
      setError(err?.message || 'Eroare.');
    } finally {
      setResponding(false);
    }
  }

  if (loading) {
    return (
      <div className="flex min-h-[50vh] items-center justify-center">
        <Loader2 className="h-8 w-8 animate-spin text-[var(--vivid-purple)]" />
      </div>
    );
  }

  return (
    <div className="mx-auto max-w-md px-4 py-16 text-center" style={{ fontFamily: 'var(--font-primary)' }}>
      {error && !invitation && (
        <div>
          <XCircle className="mx-auto mb-4 h-12 w-12 text-red-400" />
          <p className="text-lg font-semibold text-red-600">{error}</p>
        </div>
      )}

      {invitation && done === 'accepted' && (
        <div>
          <CheckCircle2 className="mx-auto mb-4 h-12 w-12 text-green-500" />
          <h2 className="mb-2 text-2xl font-bold text-green-700">Participarea confirmată!</h2>
          <p className="mb-6 text-sm text-muted-foreground" style={{ fontFamily: 'var(--font-secondary)' }}>
            Te-ai înregistrat la <strong>{invitation.activity_title}</strong>.
          </p>
          <Link
            to={`/eveniment/${invitation.activity_slug}`}
            className="inline-block rounded-lg bg-[var(--vivid-purple)] px-6 py-3 text-sm font-semibold text-white"
          >
            Vezi activitatea
          </Link>
        </div>
      )}

      {invitation && done === 'declined' && (
        <div>
          <XCircle className="mx-auto mb-4 h-12 w-12 text-muted-foreground" />
          <h2 className="mb-2 text-2xl font-bold">Invitație refuzată</h2>
          <p className="text-sm text-muted-foreground" style={{ fontFamily: 'var(--font-secondary)' }}>
            Ai refuzat invitația la {invitation.activity_title}.
          </p>
        </div>
      )}

      {invitation && !done && invitation.status !== 'sent' && (
        <div>
          <Mail className="mx-auto mb-4 h-12 w-12 text-muted-foreground" />
          <h2 className="mb-2 text-xl font-bold">Invitație deja procesată</h2>
          <p className="text-sm text-muted-foreground">Această invitație a fost deja {invitation.status === 'accepted' ? 'acceptată' : 'procesată'}.</p>
        </div>
      )}

      {invitation && !done && invitation.status === 'sent' && (
        <div>
          <Mail className="mx-auto mb-4 h-12 w-12 text-[var(--vivid-purple)]" />
          <h2 className="mb-2 text-2xl font-bold" style={{ color: 'var(--vivid-purple)' }}>
            Invitație
          </h2>
          <p className="mb-1 text-lg font-semibold">{invitation.activity_title}</p>
          <p className="mb-6 text-sm text-muted-foreground" style={{ fontFamily: 'var(--font-secondary)' }}>
            Invitație pentru: {invitation.email}
          </p>

          {error && <div className="mb-4 rounded-lg bg-red-50 px-4 py-3 text-sm text-red-700">{error}</div>}

          {!user ? (
            <div className="mb-6 rounded-lg border bg-amber-50 p-4">
              <p className="mb-3 text-sm text-amber-800" style={{ fontFamily: 'var(--font-secondary)' }}>
                Trebuie să te autentifici cu adresa <strong>{invitation.email}</strong> pentru a răspunde.
              </p>
              <Link
                to="/cont"
                className="inline-block rounded-lg bg-[var(--vivid-purple)] px-6 py-2.5 text-sm font-semibold text-white"
              >
                Conectează-te
              </Link>
            </div>
          ) : user.email.toLowerCase() !== invitation.email.toLowerCase() ? (
            <div className="mb-6 rounded-lg border bg-red-50 p-4">
              <p className="text-sm text-red-700" style={{ fontFamily: 'var(--font-secondary)' }}>
                Ești conectat ca <strong>{user.email}</strong>, dar invitația este pentru <strong>{invitation.email}</strong>.
                Conectează-te cu adresa corectă.
              </p>
            </div>
          ) : (
            <div className="space-y-3">
              <button
                onClick={() => handleRespond('accept')}
                disabled={responding}
                className="w-full rounded-lg bg-green-600 px-6 py-3 text-sm font-bold text-white transition-colors hover:bg-green-700 disabled:opacity-60"
              >
                {responding ? 'Se procesează...' : 'Accept invitația'}
              </button>
              <div>
                <textarea
                  value={declineReason}
                  onChange={(e) => setDeclineReason(e.target.value)}
                  rows={2}
                  placeholder="Motiv refuz (opțional)"
                  className="mb-2 w-full rounded-lg border px-3 py-2 text-sm"
                  style={{ fontFamily: 'var(--font-secondary)' }}
                />
                <button
                  onClick={() => handleRespond('decline')}
                  disabled={responding}
                  className="w-full rounded-lg border border-red-200 px-6 py-3 text-sm font-medium text-red-600 transition-colors hover:bg-red-50 disabled:opacity-60"
                >
                  Refuză invitația
                </button>
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
}
